import type { Worm } from '../entities/worm';
import type { GameWorld } from './types';

export interface SpawnPosition {
  x: number;
  y: number;
}

/**
 * Computes a projectile spawn position that doesn't clip into terrain.
 * Raycasts back from the ideal spawn point toward the worm center to find
 * the first non-solid position, with a 2px margin from the terrain surface.
 */
export function computeSafeSpawnPosition(
  worm: Worm,
  aimRad: number,
  spawnDistance: number,
  world: GameWorld,
): SpawnPosition {
  const bounds = worm.getBounds();
  const originX = worm.position.x;
  const originY = worm.position.y + bounds.height / 2;

  const idealX = originX + Math.cos(aimRad) * spawnDistance;
  const idealY = originY - Math.sin(aimRad) * spawnDistance;

  const terrain = world.getTerrain();

  if (!terrain.isSolid(idealX, idealY)) {
    return { x: idealX, y: idealY };
  }

  const dx = originX - idealX;
  const dy = originY - idealY;
  const rayLength = Math.sqrt(dx * dx + dy * dy);

  if (rayLength < 1) {
    return { x: originX, y: originY };
  }

  const stepX = dx / rayLength;
  const stepY = dy / rayLength;

  for (let i = 1; i <= rayLength; i++) {
    const testX = idealX + stepX * i;
    const testY = idealY + stepY * i;

    if (!terrain.isSolid(testX, testY)) {
      const marginX = testX + stepX * 2;
      const marginY = testY + stepY * 2;

      if (!terrain.isSolid(marginX, marginY)) {
        return { x: marginX, y: marginY };
      }
      return { x: testX, y: testY };
    }
  }

  return { x: originX, y: originY };
}
